import React, { useState, useMemo, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  Chip,
  TextField,
  Grid,
  MenuItem,
  Alert,
  Snackbar,
  Menu,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { DataGrid, GridActionsCellItem, GridColDef } from '@mui/x-data-grid';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  Delete as DeleteIcon,
  FilterList as FilterListIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  ReceiptLong as ReceiptLongIcon,
  AttachMoney as MoneyIcon,
  Paid as PaidIcon,
  Pending as PendingIcon,
  TrendingUp as TrendingUpIcon,
} from '@mui/icons-material';
import { useSearchParams } from 'react-router-dom';
import {
  useAllExpendituresFlat,
  useExpenditureCategories,
  useDeleteExpenditure,
  useExpendituresStats,
  useCreateExpenditure,
  useUpdateExpenditure,
  useMakePayment,
  useCreateExpenditureCategory,
  useUpdateExpenditureCategory,
  useDeleteExpenditureCategory,
} from '../hooks/useExpenditures';
import { useBirdProcurements, useBirds } from '../hooks/useBirds';
import type { Expenditure } from '../types';

// ── helpers ───────────────────────────────────────────────────────────────────

const parsePrice = (v: string | number): number => {
  if (typeof v === 'number') return v;
  return parseFloat(v.replace(/[^0-9.-]+/g, '')) || 0;
};

const fmt = (v: string | number): string => {
  const n = typeof v === 'number' ? v : parsePrice(v);
  return `GH₵ ${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const paymentStatus = (e: Expenditure): 'paid' | 'partial' | 'pending' => {
  const left = parsePrice(e.amount_left);
  if (left <= 0) return 'paid';
  return parsePrice(e.amount_paid) > 0 ? 'partial' : 'pending';
};

const expendableLabel = (e: Expenditure): string => {
  if (!e.expendable || !e.expendable_type) return '—';
  const ex = e.expendable as any;
  const t = e.expendable_type;
  if (t.includes('BirdProcurement')) return ex.identifier ?? '—';
  if (t.includes('Birds'))
    return ex.procurement?.identifier
      ? `${ex.procurement.identifier} – ${ex.color}`
      : (ex.name ?? '—');
  if (t.includes('Employee'))
    return ex.name ?? (`${ex.first_name ?? ''} ${ex.last_name ?? ''}`.trim() || '—');
  if (t.includes('MedicationTransaction')) {
    const bp = ex.bird_procurement;
    if (!bp) return '—';
    const birds: any[] = bp.birds ?? [];
    if (birds.length === 0) return bp.identifier ?? '—';
    return birds.map((b: any) => b.name).join(', ');
  }
  if (t.includes('FeedProcurement') || t.includes('FeedProduction')) {
    // Title format: "Feed expense for {flock} on {date}" — extract the flock part
    const match = e.title?.match(/^Feed expense for (.+?) on \d{4}-\d{2}-\d{2}/);
    return match ? match[1] : '—';
  }
  return '—';
};

const EMPTY_FORM = {
  category_id: -1,
  procurement_id: -1,
  birds_id: -1,
  title: '',
  unit_price: '',
  quantity: '1',
  expense_date: new Date().toISOString().split('T')[0],
  amount_paid: '',
  remarks: '',
};

const STATUS_STYLE = {
  paid: {
    label: 'Paid',
    color: '#2e7d32',
    bg: 'rgba(46,125,50,0.12)',
    border: 'rgba(46,125,50,0.3)',
  },
  partial: {
    label: 'Partial',
    color: '#e65100',
    bg: 'rgba(230,81,0,0.12)',
    border: 'rgba(230,81,0,0.3)',
  },
  pending: {
    label: 'Pending',
    color: '#c62828',
    bg: 'rgba(198,40,40,0.10)',
    border: 'rgba(198,40,40,0.3)',
  },
};

// ─────────────────────────────────────────────────────────────────────────────

const AllExpendituresPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedExp, setSelectedExp] = useState<Expenditure | null>(null);
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });

  const [editOpen, setEditOpen] = useState(false);
  const [editingExp, setEditingExp] = useState<Expenditure | null>(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payExp, setPayExp] = useState<Expenditure | null>(null);
  const [delOpen, setDelOpen] = useState(false);
  const [deletingExp, setDeletingExp] = useState<Expenditure | null>(null);
  const [catOpen, setCatOpen] = useState(false);

  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [payAmt, setPayAmt] = useState('');
  const [catForm, setCatForm] = useState({ name: '', description: '' });
  const [editingCat, setEditingCat] = useState<{
    id: number;
    name: string;
    description?: string;
  } | null>(null);
  const [deletingCat, setDeletingCat] = useState<{ id: number; name: string } | null>(null);

  const [titleInput, setTitleInput] = useState(searchParams.get('title') || '');
  const titleDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (titleDebounce.current) clearTimeout(titleDebounce.current);
    titleDebounce.current = setTimeout(() => {
      setFilters((p) => ({ ...p, title: titleInput }));
      setPaginationModel((p) => ({ ...p, page: 0 }));
    }, 400);
    return () => {
      if (titleDebounce.current) clearTimeout(titleDebounce.current);
    };
  }, [titleInput]);

  const [filters, setFilters] = useState({
    title: searchParams.get('title') || '',
    category_id: searchParams.get('category_id') || '',
    procurement_id: searchParams.get('procurement_id') || '',
    date_from: searchParams.get('date_from') || '',
    date_to: searchParams.get('date_to') || '',
    payment_status: searchParams.get('payment_status') || '',
  });

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({ open: false, message: '', severity: 'info' });

  React.useEffect(() => {
    setSearchParams((prev) => {
      const p = new URLSearchParams(prev.toString());
      Object.entries(filters).forEach(([k, v]) => (v ? p.set(k, v) : p.delete(k)));
      return p;
    });
  }, [filters, setSearchParams]);

  const filterParams = useMemo(
    () => ({
      ...(filters.title ? { title: filters.title } : {}),
      ...(filters.category_id ? { category_id: filters.category_id } : {}),
      ...(filters.procurement_id
        ? { expendable_type: 'App\\Models\\BirdProcurement', expendable_id: filters.procurement_id }
        : {}),
      ...(filters.payment_status ? { payment_status: filters.payment_status } : {}),
      ...(filters.date_from ? { date_from: filters.date_from } : {}),
      ...(filters.date_to ? { date_to: filters.date_to } : {}),
    }),
    [filters]
  );

  const apiParams = useMemo(
    () => ({
      page: paginationModel.page + 1,
      per_page: paginationModel.pageSize,
      ...filterParams,
    }),
    [paginationModel, filterParams]
  );

  const {
    data: expData,
    isLoading,
    error,
    isFetching,
    refetch,
  } = useAllExpendituresFlat(apiParams);
  const { data: stats } = useExpendituresStats();
  const { data: filteredStats } = useExpendituresStats(
    Object.keys(filterParams).length ? filterParams : undefined
  );
  const { data: categories } = useExpenditureCategories();
  const { data: procData } = useBirdProcurements({ per_page: 1000 });
  const { data: birdsData } = useBirds(
    form.procurement_id !== -1 ? { procurement_id: form.procurement_id, per_page: 1000 } : undefined
  );

  const deleteMut = useDeleteExpenditure(apiParams);
  const createMut = useCreateExpenditure(apiParams);
  const updateMut = useUpdateExpenditure(apiParams);
  const payMut = useMakePayment(apiParams);
  const createCatMut = useCreateExpenditureCategory();
  const updateCatMut = useUpdateExpenditureCategory();
  const deleteCatMut = useDeleteExpenditureCategory();

  const procs = useMemo(() => procData?.data || [], [procData]);
  const expenditures = useMemo(() => expData?.data || [], [expData]);
  const totalRecords = expData?.meta?.total ?? 0;

  const calcTotal = useMemo(
    () => parseFloat(form.unit_price || '0') * parseInt(form.quantity || '1'),
    [form.unit_price, form.quantity]
  );
  const hasFilters = Object.values(filters).some(Boolean);

  const toast = (message: string, severity: 'error' | 'warning' | 'info' | 'success') =>
    setSnackbar({ open: true, message, severity });

  const setFilter = (field: string, value: string) => {
    setFilters((p) => ({ ...p, [field]: value }));
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const clearFilters = () => {
    setTitleInput('');
    setFilters({
      title: '',
      category_id: '',
      procurement_id: '',
      date_from: '',
      date_to: '',
      payment_status: '',
    });
    setPaginationModel((p) => ({ ...p, page: 0 }));
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedExp(null);
  };

  // edit
  const openEdit = (exp?: Expenditure) => {
    if (exp) {
      setEditingExp(exp);
      const isBirds = exp.expendable_type?.includes('Birds');
      const isProc = exp.expendable_type?.includes('BirdProcurement');
      setForm({
        category_id: exp.category_id,
        procurement_id: isProc
          ? (exp.expendable_id ?? -1)
          : isBirds
            ? ((exp.expendable as any)?.procurement_id ?? -1)
            : -1,
        birds_id: isBirds ? (exp.expendable_id ?? -1) : -1,
        title: exp.title,
        unit_price: parsePrice(exp.unit_price).toString(),
        quantity: exp.quantity.toString(),
        expense_date: exp.expense_date.split('T')[0],
        amount_paid: parsePrice(exp.amount_paid).toString(),
        remarks: exp.remarks || '',
      });
    } else {
      setEditingExp(null);
      setForm({ ...EMPTY_FORM });
    }
    setEditOpen(true);
    closeMenu();
  };

  const closeEdit = () => {
    setEditOpen(false);
    setEditingExp(null);
    setForm({ ...EMPTY_FORM });
  };

  const handleSubmit = async () => {
    if (form.category_id === -1) {
      toast('Select a category', 'warning');
      return;
    }
    if (!form.title.trim()) {
      toast('Enter a description', 'warning');
      return;
    }
    if (!form.unit_price || parseFloat(form.unit_price) <= 0) {
      toast('Enter a valid unit price', 'warning');
      return;
    }
    const amtPaid = parseFloat(form.amount_paid || '0');
    if (amtPaid > calcTotal) {
      toast('Amount paid cannot exceed total', 'error');
      return;
    }
    const data: Record<string, any> = {
      category_id: form.category_id,
      title: form.title,
      unit_price: parseFloat(form.unit_price),
      quantity: parseInt(form.quantity),
      expense_date: form.expense_date,
      amount_paid: amtPaid,
      remarks: form.remarks || undefined,
    };
    if (form.procurement_id !== -1) {
      data.expendable_type = 'App\\Models\\BirdProcurement';
      data.expendable_id = form.procurement_id;
    } else if (form.birds_id !== -1) {
      data.expendable_type = 'App\\Models\\Birds';
      data.expendable_id = form.birds_id;
    }
    try {
      if (editingExp) {
        await updateMut.mutateAsync({ id: editingExp.id, data });
        toast('Expenditure updated', 'success');
      } else {
        await createMut.mutateAsync(data);
        toast('Expenditure recorded', 'success');
      }
      closeEdit();
    } catch {
      toast('Failed to save expenditure', 'error');
    }
  };

  // payment
  const openPay = (exp: Expenditure) => {
    setPayExp(exp);
    setPayAmt(Math.max(0, parsePrice(exp.amount_left)).toString());
    setPayOpen(true);
    closeMenu();
  };

  const handlePay = async () => {
    if (!payExp) return;
    const amount = parseFloat(payAmt);
    const balance = Math.abs(parsePrice(payExp.amount_left));
    if (!amount || amount <= 0) {
      toast('Enter a valid amount', 'warning');
      return;
    }
    if (amount > balance) {
      toast('Payment exceeds outstanding balance', 'error');
      return;
    }
    try {
      await payMut.mutateAsync({ id: payExp.id, data: { payment_amount: amount } });
      toast('Payment recorded', 'success');
      setPayOpen(false);
      setPayExp(null);
    } catch {
      toast('Failed to record payment', 'error');
    }
  };

  // delete
  const openDel = (exp: Expenditure) => {
    setDeletingExp(exp);
    setDelOpen(true);
    closeMenu();
  };

  const handleDelete = async () => {
    if (!deletingExp) return;
    try {
      await deleteMut.mutateAsync(deletingExp.id);
      toast('Expenditure deleted', 'success');
      setDelOpen(false);
      setDeletingExp(null);
    } catch {
      toast('Failed to delete expenditure', 'error');
    }
  };

  // categories
  const handleSaveCat = async () => {
    if (!catForm.name.trim()) return;
    try {
      if (editingCat) {
        await updateCatMut.mutateAsync({ id: editingCat.id, data: catForm });
        toast('Category updated', 'success');
      } else {
        await createCatMut.mutateAsync(catForm);
        toast('Category created', 'success');
      }
      setCatForm({ name: '', description: '' });
      setEditingCat(null);
    } catch {
      toast('Failed to save category', 'error');
    }
  };

  const handleDeleteCat = async () => {
    if (!deletingCat) return;
    try {
      await deleteCatMut.mutateAsync(deletingCat.id);
      toast('Category deleted', 'success');
      setDeletingCat(null);
    } catch {
      toast('Failed to delete category', 'error');
    }
  };

  // ── columns ───────────────────────────────────────────────────────────────────
  const columns: GridColDef[] = [
    {
      field: 'expense_date',
      headerName: 'Date',
      width: 100,
      valueFormatter: (v) =>
        new Date(v).toLocaleDateString('en-GB', {
          day: '2-digit',
          month: 'short',
          year: '2-digit',
        }),
    },
    {
      field: 'category',
      headerName: 'Category',
      width: 130,
      renderCell: (p) => {
        const name = p.row.category?.name;
        return name ? (
          <Chip
            label={name}
            size="small"
            sx={{
              fontSize: '0.7rem',
              height: 22,
              fontWeight: 600,
              bgcolor: 'rgba(255,152,0,0.12)',
              color: '#ff9800',
              border: '1px solid rgba(255,152,0,0.3)',
            }}
          />
        ) : (
          <Typography variant="body2" color="text.disabled">
            —
          </Typography>
        );
      },
    },
    {
      field: 'title',
      headerName: 'Description',
      flex: 1,
      minWidth: 180,
      renderCell: (p) => (
        <Typography variant="body2" fontWeight={500}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'expendable',
      headerName: 'Relates To',
      width: 160,
      valueGetter: (_v, row) => expendableLabel(row),
      renderCell: (p) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {p.value || '—'}
        </Typography>
      ),
    },
    {
      field: 'total_amount',
      headerName: 'Total',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2" fontWeight={700}>
          {fmt(p.value)}
        </Typography>
      ),
    },
    {
      field: 'amount_paid',
      headerName: 'Paid',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => (
        <Typography variant="body2" fontWeight={600} color="success.main">
          {fmt(p.value)}
        </Typography>
      ),
    },
    {
      field: 'amount_left',
      headerName: 'Balance',
      width: 130,
      type: 'number',
      align: 'right',
      headerAlign: 'right',
      renderCell: (p) => {
        const b = parsePrice(p.value);
        return (
          <Typography
            variant="body2"
            fontWeight={b > 0 ? 700 : 400}
            color={b > 0 ? 'error.main' : 'text.disabled'}
          >
            {fmt(p.value)}
          </Typography>
        );
      },
    },
    {
      field: 'payment_status',
      headerName: 'Status',
      width: 90,
      align: 'center',
      headerAlign: 'center',
      renderCell: (p) => {
        const s = paymentStatus(p.row);
        const c = STATUS_STYLE[s];
        return (
          <Chip
            label={c.label}
            size="small"
            sx={{
              fontSize: '0.68rem',
              height: 22,
              fontWeight: 700,
              color: c.color,
              bgcolor: c.bg,
              border: `1px solid ${c.border}`,
            }}
          />
        );
      },
    },
    {
      field: 'added_by',
      headerName: 'Added By',
      width: 115,
      valueGetter: (_v, row) => row.added_by?.name || '—',
      renderCell: (p) => (
        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
          {p.value}
        </Typography>
      ),
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: '',
      width: 50,
      getActions: (p) => [
        <GridActionsCellItem
          key="m"
          icon={<MoreVertIcon />}
          label="Actions"
          onClick={(e) => {
            setAnchorEl(e.currentTarget as any);
            setSelectedExp(p.row);
          }}
        />,
      ],
    },
  ];

  // ── render ────────────────────────────────────────────────────────────────────
  if (error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <Box textAlign="center" maxWidth={480}>
          <Alert severity="error" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Failed to Load Expenditures
            </Typography>
            <Typography variant="body2">
              {error instanceof Error ? error.message : 'An unexpected error occurred.'}
            </Typography>
          </Alert>
          <Button variant="contained" onClick={() => refetch()} sx={{ color: '#fff' }}>
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  const STAT_CARDS = [
    {
      label: 'Total Expenditures',
      value: stats ? fmt(Number(stats.total_expenses) / 100) : '—',
      icon: <MoneyIcon sx={{ fontSize: 34, color: '#2196f3', opacity: 0.9 }} />,
      color: '#2196f3',
    },
    {
      label: 'Amount Paid',
      value: stats ? fmt(Number(stats.total_paid) / 100) : '—',
      icon: <PaidIcon sx={{ fontSize: 34, color: '#66bb6a', opacity: 0.9 }} />,
      color: '#66bb6a',
    },
    {
      label: 'Outstanding',
      value: stats ? fmt(Number(stats.total_pending) / 100) : '—',
      icon: <PendingIcon sx={{ fontSize: 34, color: '#f44336', opacity: 0.9 }} />,
      color: '#f44336',
    },
    {
      label: 'Transactions',
      value: stats?.total_transactions?.toLocaleString() ?? '—',
      icon: <TrendingUpIcon sx={{ fontSize: 34, color: '#ff9800', opacity: 0.9 }} />,
      color: '#ff9800',
    },
  ];

  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 1.5 } };
  const chipSx = {
    bgcolor: 'rgba(255,152,0,0.1)',
    color: '#ff9800',
    '& .MuiChip-deleteIcon': { color: '#ff9800' },
  };

  return (
    <Box sx={{ p: 3 }}>
      {isFetching && (
        <LinearProgress
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 9999,
            height: 3,
            '& .MuiLinearProgress-bar': { background: 'linear-gradient(90deg, #ff9800, #ffa726)' },
          }}
        />
      )}

      {/* Header */}
      <Box
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}
      >
        <Box>
          <Typography variant="h5" fontWeight={800} sx={{ letterSpacing: '-0.5px' }}>
            Expenditures
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {totalRecords > 0 ? (
              <>
                <strong style={{ color: '#ff9800' }}>{totalRecords.toLocaleString()}</strong> record
                {totalRecords !== 1 ? 's' : ''}
                {hasFilters ? ' matching current filters' : ' total'}
              </>
            ) : isLoading ? (
              'Loading…'
            ) : (
              'No records match the current filters'
            )}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setCatOpen(true)}
            sx={{
              borderColor: 'rgba(255,152,0,0.5)',
              color: '#ff9800',
              '&:hover': { borderColor: '#ff9800', bgcolor: 'rgba(255,152,0,0.08)' },
            }}
          >
            Manage Categories
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => openEdit()}
            sx={{
              background: 'linear-gradient(135deg, #ff9800, #ffa726)',
              color: '#fff',
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #f57c00, #ff9800)' },
            }}
          >
            New Expenditure
          </Button>
        </Box>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {STAT_CARDS.map(({ label, value, icon, color }) => (
          <Grid key={label} size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper
              sx={{
                p: 2.5,
                borderRadius: 2,
                background: 'linear-gradient(135deg, #1a1a1a 0%, #262626 100%)',
                border: '1px solid rgba(255,152,0,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: 'rgba(255,152,0,0.8)',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    fontSize: '0.7rem',
                  }}
                >
                  {label}
                </Typography>
                <Typography variant="h6" fontWeight={800} sx={{ color, lineHeight: 1.2, mt: 0.25 }}>
                  {value}
                </Typography>
              </Box>
              {icon}
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 2,
          mb: 2,
          borderRadius: 2,
          background: 'rgba(255,152,0,0.03)',
          border: '1px solid rgba(255,152,0,0.15)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
          <FilterListIcon sx={{ color: '#ff9800', fontSize: 18 }} />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            sx={{
              color: '#ff9800',
              fontSize: '0.75rem',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}
          >
            Filters
          </Typography>
          {hasFilters && (
            <Button
              size="small"
              onClick={clearFilters}
              sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.72rem' }}
            >
              Clear All
            </Button>
          )}
        </Box>
        <Grid container spacing={1.5}>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              placeholder="Search by description…"
              value={titleInput}
              size="small"
              sx={inputSx}
              slotProps={{
                input: {
                  startAdornment: <SearchIcon sx={{ color: '#ff9800', fontSize: 18, mr: 0.75 }} />,
                },
              }}
              onChange={(e) => setTitleInput(e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Autocomplete
              options={categories || []}
              getOptionLabel={(o) => o.name}
              value={categories?.find((c) => c.id.toString() === filters.category_id) ?? null}
              onChange={(_, v) => setFilter('category_id', v?.id.toString() ?? '')}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              size="small"
              renderInput={(p) => <TextField {...p} label="Category" sx={inputSx} />}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              fullWidth
              label="Procurement Batch"
              value={filters.procurement_id}
              size="small"
              sx={inputSx}
              onChange={(e) => setFilter('procurement_id', e.target.value)}
            >
              <MenuItem value="">All Batches</MenuItem>
              {procs.map((p: any) => (
                <MenuItem key={p.id} value={p.id.toString()}>
                  {p.identifier}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              fullWidth
              label="From"
              type="date"
              value={filters.date_from}
              size="small"
              sx={inputSx}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setFilter('date_from', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              fullWidth
              label="To"
              type="date"
              value={filters.date_to}
              size="small"
              sx={inputSx}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setFilter('date_to', e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4, md: 2 }}>
            <TextField
              select
              fullWidth
              label="Status"
              value={filters.payment_status}
              size="small"
              sx={inputSx}
              onChange={(e) => setFilter('payment_status', e.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="partial">Partial</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </TextField>
          </Grid>
        </Grid>
        {hasFilters && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
            {filters.title && (
              <Chip
                size="small"
                sx={chipSx}
                label={`Description: "${filters.title}"`}
                onDelete={() => {
                  setTitleInput('');
                  setFilter('title', '');
                }}
              />
            )}
            {filters.category_id && (
              <Chip
                size="small"
                sx={chipSx}
                onDelete={() => setFilter('category_id', '')}
                label={`Category: ${categories?.find((c) => c.id.toString() === filters.category_id)?.name ?? filters.category_id}`}
              />
            )}
            {filters.procurement_id && (
              <Chip
                size="small"
                sx={chipSx}
                onDelete={() => setFilter('procurement_id', '')}
                label={`Batch: ${(procs as any[]).find((p) => p.id.toString() === filters.procurement_id)?.identifier ?? filters.procurement_id}`}
              />
            )}
            {filters.date_from && (
              <Chip
                size="small"
                sx={chipSx}
                label={`From: ${filters.date_from}`}
                onDelete={() => setFilter('date_from', '')}
              />
            )}
            {filters.date_to && (
              <Chip
                size="small"
                sx={chipSx}
                label={`To: ${filters.date_to}`}
                onDelete={() => setFilter('date_to', '')}
              />
            )}
            {filters.payment_status && (
              <Chip
                size="small"
                sx={chipSx}
                label={`Status: ${filters.payment_status}`}
                onDelete={() => setFilter('payment_status', '')}
              />
            )}
          </Box>
        )}
      </Paper>

      {/* DataGrid */}
      <Paper
        elevation={0}
        sx={{ overflow: 'hidden', borderRadius: 2, border: '1px solid rgba(255,152,0,0.15)' }}
      >
        {expenditures.length > 0 && (
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              borderBottom: '1px solid rgba(255,152,0,0.12)',
              background: 'linear-gradient(90deg, rgba(255,152,0,0.06), rgba(255,152,0,0.02))',
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{
                mr: 'auto',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                fontSize: '0.68rem',
              }}
            >
              {hasFilters ? 'Filtered' : 'All'} · {totalRecords.toLocaleString()} record
              {totalRecords !== 1 ? 's' : ''}
            </Typography>
            {[
              {
                label: 'Total',
                value: filteredStats ? Number(filteredStats.total_expenses) / 100 : 0,
                color: 'text.primary',
              },
              {
                label: 'Paid',
                value: filteredStats ? Number(filteredStats.total_paid) / 100 : 0,
                color: 'success.main',
              },
              {
                label: 'Balance',
                value: filteredStats ? Number(filteredStats.total_pending) / 100 : 0,
                color:
                  filteredStats && Number(filteredStats.total_pending) > 0
                    ? 'error.main'
                    : 'text.disabled',
              },
            ].map(({ label, value, color }, i) => (
              <React.Fragment key={label}>
                {i > 0 && <Divider orientation="vertical" flexItem />}
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                    sx={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}
                  >
                    {label}
                  </Typography>
                  <Typography variant="body2" fontWeight={700} color={color}>
                    {fmt(value)}
                  </Typography>
                </Box>
              </React.Fragment>
            ))}
          </Box>
        )}

        <DataGrid
          rows={expenditures}
          columns={columns}
          loading={isLoading}
          pageSizeOptions={[10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          paginationMode="server"
          rowCount={totalRecords}
          disableRowSelectionOnClick
          rowHeight={52}
          slots={{
            noRowsOverlay: () => (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                height="100%"
                gap={1.5}
                sx={{ color: 'text.secondary', py: 6 }}
              >
                <ReceiptLongIcon sx={{ fontSize: 52, opacity: 0.2 }} />
                <Typography variant="body1" fontWeight={500}>
                  No expenditures found
                </Typography>
                {hasFilters && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={clearFilters}
                    sx={{ mt: 0.5, borderColor: 'rgba(255,152,0,0.4)', color: '#ff9800' }}
                  >
                    Clear filters
                  </Button>
                )}
              </Box>
            ),
          }}
          sx={{
            border: 'none',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid rgba(255,152,0,0.06)',
              display: 'flex',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders': {
              borderBottom: '2px solid rgba(255,152,0,0.25)',
              '& .MuiDataGrid-columnHeaderTitle': {
                fontWeight: 700,
                fontSize: '0.72rem',
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
                color: '#e65100',
              },
            },
            '& .MuiDataGrid-row:hover': { bgcolor: 'rgba(255,152,0,0.03)' },
            '& .MuiDataGrid-row:nth-of-type(even)': { bgcolor: 'rgba(255,255,255,0.01)' },
            '& .MuiDataGrid-footerContainer': { borderTop: '1px solid rgba(255,152,0,0.1)' },
          }}
        />
      </Paper>

      {/* Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        slotProps={{
          paper: {
            sx: { borderRadius: 2, border: '1px solid rgba(255,152,0,0.2)', minWidth: 165 },
          },
        }}
      >
        <MenuItem onClick={() => selectedExp && openEdit(selectedExp)}>
          <EditIcon fontSize="small" sx={{ mr: 1.5, color: '#ff9800' }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => selectedExp && openPay(selectedExp)}
          disabled={selectedExp ? paymentStatus(selectedExp) === 'paid' : false}
        >
          <PaymentIcon fontSize="small" sx={{ mr: 1.5, color: '#66bb6a' }} /> Record Payment
        </MenuItem>
        <Divider sx={{ my: 0.5, borderColor: 'rgba(255,152,0,0.1)' }} />
        <MenuItem onClick={() => selectedExp && openDel(selectedExp)} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete
        </MenuItem>
      </Menu>

      {/* Add / Edit Dialog */}
      <Dialog
        open={editOpen}
        onClose={closeEdit}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, border: '1px solid rgba(255,152,0,0.2)' } } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(255,152,0,0.12), rgba(255,152,0,0.04))',
            borderBottom: '1px solid rgba(255,152,0,0.15)',
            color: '#ff9800',
            fontWeight: 700,
          }}
        >
          {editingExp ? 'Edit Expenditure' : 'New Expenditure'}
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 0.5 }}>
            <Autocomplete
              options={categories || []}
              getOptionLabel={(o) => o.name}
              value={categories?.find((c) => c.id === form.category_id) || null}
              onChange={(_, v) => setForm((p) => ({ ...p, category_id: v?.id || -1 }))}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              renderInput={(p) => <TextField {...p} label="Category" required size="small" />}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={procData?.data || []}
                  getOptionLabel={(o) => o.identifier}
                  value={procData?.data?.find((p) => p.id === form.procurement_id) || null}
                  onChange={(_, v) =>
                    setForm((p) => ({ ...p, procurement_id: v?.id || -1, birds_id: -1 }))
                  }
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  renderInput={(p) => (
                    <TextField {...p} label="Procurement (optional)" size="small" />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <Autocomplete
                  options={birdsData?.data || []}
                  getOptionLabel={(o: any) => `${o.identifier} – ${o.birds_type} (${o.color})`}
                  value={(birdsData?.data as any[])?.find((b) => b.id === form.birds_id) || null}
                  onChange={(_, v: any) => setForm((p) => ({ ...p, birds_id: v?.id || -1 }))}
                  disabled={form.procurement_id === -1}
                  isOptionEqualToValue={(o: any, v: any) => o.id === v.id}
                  renderInput={(p) => (
                    <TextField
                      {...p}
                      label="Bird batch (optional)"
                      size="small"
                      helperText={form.procurement_id === -1 ? 'Select procurement first' : ''}
                    />
                  )}
                />
              </Grid>
            </Grid>
            <TextField
              label="Description"
              value={form.title}
              size="small"
              required
              fullWidth
              multiline
              rows={2}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
            />
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 5 }}>
                <TextField
                  label="Unit Price (GH₵)"
                  type="number"
                  value={form.unit_price}
                  size="small"
                  required
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  onChange={(e) => setForm((p) => ({ ...p, unit_price: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  label="Qty"
                  type="number"
                  value={form.quantity}
                  size="small"
                  required
                  fullWidth
                  slotProps={{ htmlInput: { min: 1 } }}
                  onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 4 }}>
                <TextField
                  label="Total"
                  value={fmt(calcTotal)}
                  size="small"
                  fullWidth
                  slotProps={{ input: { readOnly: true } }}
                  sx={{ '& input': { fontWeight: 700, color: '#ff9800' } }}
                />
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Expense Date"
                  type="date"
                  value={form.expense_date}
                  size="small"
                  required
                  fullWidth
                  slotProps={{ inputLabel: { shrink: true } }}
                  onChange={(e) => setForm((p) => ({ ...p, expense_date: e.target.value }))}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  label="Amount Paid (GH₵)"
                  type="number"
                  value={form.amount_paid}
                  size="small"
                  fullWidth
                  slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                  helperText={`Max: ${fmt(calcTotal)}`}
                  onChange={(e) => setForm((p) => ({ ...p, amount_paid: e.target.value }))}
                />
              </Grid>
            </Grid>
            <TextField
              label="Remarks (optional)"
              value={form.remarks}
              size="small"
              fullWidth
              multiline
              rows={2}
              onChange={(e) => setForm((p) => ({ ...p, remarks: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid rgba(255,152,0,0.1)', gap: 1 }}>
          <Button
            onClick={closeEdit}
            variant="outlined"
            sx={{ borderColor: 'rgba(255,255,255,0.15)', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={createMut.isPending || updateMut.isPending}
            startIcon={
              createMut.isPending || updateMut.isPending ? (
                <CircularProgress size={15} color="inherit" />
              ) : undefined
            }
            sx={{
              background: 'linear-gradient(135deg, #ff9800, #ffa726)',
              color: '#fff',
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #f57c00, #ff9800)' },
            }}
          >
            {editingExp ? 'Update' : 'Save'} Expenditure
          </Button>
        </DialogActions>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog
        open={payOpen}
        onClose={() => setPayOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, border: '1px solid rgba(76,175,80,0.25)' } } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(76,175,80,0.12), rgba(76,175,80,0.04))',
            borderBottom: '1px solid rgba(76,175,80,0.15)',
            color: '#66bb6a',
            fontWeight: 700,
          }}
        >
          Record Payment
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          {payExp && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: 'rgba(76,175,80,0.05)',
                  border: '1px solid rgba(76,175,80,0.2)',
                  borderRadius: 1.5,
                }}
              >
                <Typography variant="caption" color="text.secondary" display="block">
                  Outstanding Balance
                </Typography>
                <Typography variant="h6" fontWeight={800} color="error.main">
                  {fmt(Math.abs(parsePrice(payExp.amount_left)))}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {payExp.title}
                </Typography>
              </Paper>
              <TextField
                label="Payment Amount (GH₵)"
                type="number"
                value={payAmt}
                size="small"
                fullWidth
                required
                slotProps={{ htmlInput: { min: 0, step: 0.01 } }}
                onChange={(e) => setPayAmt(e.target.value)}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, borderTop: '1px solid rgba(76,175,80,0.1)', gap: 1 }}>
          <Button
            onClick={() => setPayOpen(false)}
            variant="outlined"
            sx={{ borderColor: 'rgba(255,255,255,0.15)', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handlePay}
            variant="contained"
            disabled={payMut.isPending}
            startIcon={
              payMut.isPending ? <CircularProgress size={15} color="inherit" /> : undefined
            }
            sx={{
              background: 'linear-gradient(135deg, #4caf50, #66bb6a)',
              color: '#fff',
              fontWeight: 700,
              '&:hover': { background: 'linear-gradient(135deg, #388e3c, #4caf50)' },
            }}
          >
            Confirm Payment
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog
        open={delOpen}
        onClose={() => setDelOpen(false)}
        maxWidth="xs"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, border: '1px solid rgba(244,67,54,0.2)' } } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <WarningIcon color="error" fontSize="small" /> Delete Expenditure
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Permanently delete <strong>&ldquo;{deletingExp?.title}&rdquo;</strong>?
          </Typography>
          {deletingExp && parsePrice(deletingExp.total_amount) > 0 && (
            <Alert severity="warning" icon={false} sx={{ mt: 2 }}>
              Removes <strong>{fmt(deletingExp.total_amount)}</strong> from records. Cannot be
              undone.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button
            onClick={() => setDelOpen(false)}
            variant="outlined"
            size="small"
            sx={{ borderColor: 'rgba(255,255,255,0.15)', color: 'text.secondary' }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            size="small"
            disabled={deleteMut.isPending}
            startIcon={
              deleteMut.isPending ? <CircularProgress size={14} color="inherit" /> : undefined
            }
            sx={{ color: '#fff', fontWeight: 700 }}
          >
            {deleteMut.isPending ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category Management Dialog */}
      <Dialog
        open={catOpen}
        onClose={() => {
          setCatOpen(false);
          setEditingCat(null);
          setDeletingCat(null);
          setCatForm({ name: '', description: '' });
        }}
        maxWidth="sm"
        fullWidth
        slotProps={{ paper: { sx: { borderRadius: 2, border: '1px solid rgba(255,152,0,0.2)' } } }}
      >
        <DialogTitle
          sx={{
            background: 'linear-gradient(135deg, rgba(255,152,0,0.12), rgba(255,152,0,0.04))',
            borderBottom: '1px solid rgba(255,152,0,0.15)',
            color: '#ff9800',
            fontWeight: 700,
          }}
        >
          Expenditure Categories
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5 }}>
          <Box
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 1.5,
              border: '1px solid rgba(255,152,0,0.2)',
              bgcolor: 'rgba(255,152,0,0.03)',
            }}
          >
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: '#ff9800' }}>
              {editingCat ? 'Edit Category' : 'New Category'}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <TextField
                label="Name"
                size="small"
                value={catForm.name}
                required
                autoFocus
                fullWidth
                onChange={(e) => setCatForm((p) => ({ ...p, name: e.target.value }))}
              />
              <TextField
                label="Description (optional)"
                size="small"
                value={catForm.description}
                fullWidth
                multiline
                rows={2}
                onChange={(e) => setCatForm((p) => ({ ...p, description: e.target.value }))}
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                {editingCat && (
                  <Button
                    size="small"
                    onClick={() => {
                      setEditingCat(null);
                      setCatForm({ name: '', description: '' });
                    }}
                  >
                    Cancel
                  </Button>
                )}
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSaveCat}
                  disabled={
                    !catForm.name.trim() || createCatMut.isPending || updateCatMut.isPending
                  }
                  sx={{ background: 'linear-gradient(135deg, #ff9800, #ffa726)', color: '#fff' }}
                >
                  {editingCat ? 'Update' : 'Add'} Category
                </Button>
              </Box>
            </Box>
          </Box>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              mb: 1,
              display: 'block',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
              fontWeight: 600,
            }}
          >
            Existing ({categories?.length ?? 0})
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {categories?.map((cat) =>
              deletingCat?.id === cat.id ? (
                <Box
                  key={cat.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    borderRadius: 1.5,
                    border: '1px solid rgba(244,67,54,0.3)',
                    bgcolor: 'rgba(244,67,54,0.05)',
                  }}
                >
                  <Typography variant="body2" color="error.main" sx={{ flex: 1 }}>
                    Delete &ldquo;{cat.name}&rdquo;? Cannot be undone.
                  </Typography>
                  <Button size="small" onClick={() => setDeletingCat(null)}>
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    color="error"
                    variant="contained"
                    disabled={deleteCatMut.isPending}
                    onClick={handleDeleteCat}
                    sx={{ color: '#fff' }}
                  >
                    Delete
                  </Button>
                </Box>
              ) : (
                <Box
                  key={cat.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1,
                    px: 1.5,
                    borderRadius: 1.5,
                    '&:hover': { bgcolor: 'rgba(255,152,0,0.05)' },
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>
                      {cat.name}
                    </Typography>
                    {cat.description && (
                      <Typography variant="caption" color="text.secondary">
                        {cat.description}
                      </Typography>
                    )}
                  </Box>
                  <IconButton
                    size="small"
                    sx={{ color: '#ff9800' }}
                    onClick={() => {
                      setEditingCat(cat);
                      setCatForm({ name: cat.name, description: cat.description || '' });
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    sx={{ color: 'error.main' }}
                    onClick={() => setDeletingCat({ id: cat.id, name: cat.name })}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Box>
              )
            )}
            {!categories?.length && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ textAlign: 'center', py: 2 }}
              >
                No categories yet. Add one above.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, borderTop: '1px solid rgba(255,152,0,0.1)' }}>
          <Button
            onClick={() => {
              setCatOpen(false);
              setEditingCat(null);
              setDeletingCat(null);
              setCatForm({ name: '', description: '' });
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AllExpendituresPage;
